// ============================================================================
// NOTES MARKDOWN → HTML
// ----------------------------------------------------------------------------
// Pure, dependency-free converter used by Stage 1's inspector-notes editor to
// render imported text (RFQ/scope docs, video/audio transcripts, assessor-notes
// DOCX) as light formatting HTML. Extracted verbatim from Stage1DataEntry.jsx
// on 2026-06-02 so the freeze fix below is unit-testable in isolation.
//
// ─── FREEZE FIX (hotfix v5.41.44, 2026-06-02) ──────────────────────────────
// A line that LEADS with `---` / `___` / `***` but is NEITHER a pure horizontal
// rule (`---` alone) NOR a `- ` list item used to consume nothing:
//   • the <hr> test requires the WHOLE line be rule chars  → fails on trailing text
//   • the list test requires `- ` (dash + SPACE)           → fails on `--`
//   • the paragraph collector's own exclusion regex rejects any line starting
//     with `-{3,}` / `_{3,}` / `*{3,}`                       → refuses to consume it
// So `i` never advanced and `while (i < lines.length)` spun forever — a hard,
// permanent main-thread freeze ("Page Unresponsive", force-kill required).
//
// It fired on every Stage 1 import where the inspector notes were already
// non-empty (e.g. a video/audio transcript landed first) when an assessor-notes
// document was injected wrapped as `--- <filename> ---` — e.g. the line
// `--- Site Report.docx ---`. The audio/video wrappers escaped because
// formatTranscriptBlocks rewrites their `--- Audio:/VIDEO TRANSCRIPT ---`
// markers into headings first; a plain document filename has no such keyword.
//
// Two independent guards now make this impossible:
//   1. the paragraph branch ALWAYS consumes the current line first, and
//   2. a top-of-loop progress check force-advances `i` if any branch ever
//      leaves it unchanged (belt-and-braces against future regex changes).
// ============================================================================

export function markdownToHtml(text: unknown): string {
    if (!text || typeof text !== 'string') return '';
    const esc = (s: string): string => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Apply inline formatting to a line of text
    const inlineFormat = (line: string): string => {
        let s = esc(line);
        // Bold: **text** or __text__
        s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        s = s.replace(/__(.+?)__/g, '<strong>$1</strong>');
        // Italic: *text* or _text_ (not inside bold)
        s = s.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
        s = s.replace(/(?<!_)_([^_]+?)_(?!_)/g, '<em>$1</em>');
        // Inline code: `code`
        s = s.replace(/`([^`]+?)`/g, '<code style="background:rgba(0,0,0,0.06);padding:1px 4px;border-radius:3px;font-size:0.9em;">$1</code>');
        return s;
    };

    const lines = text.split('\n');
    const html: string[] = [];
    let i = 0;
    let lastI = -1;

    while (i < lines.length) {
        // Guard 2 — progress: if the previous iteration consumed nothing,
        // force `i` forward so a malformed line can never spin this loop.
        if (i === lastI) { i++; continue; }
        lastI = i;

        const line = lines[i]!;
        const trimmed = line.trim();

        // Empty line → skip (paragraph break handled by block logic)
        if (!trimmed) { i++; continue; }

        // Horizontal rule
        if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
            html.push('<hr style="border:none;border-top:1px solid #d1d5db;margin:12px 0;"/>');
            i++; continue;
        }

        // Headers: # ## ###
        if (/^#{1,3}\s/.test(trimmed)) {
            // The preceding .test guarantees the match + capture group exist.
            const level = trimmed.match(/^(#{1,3})/)![1]!.length;
            const headerText = trimmed.replace(/^#{1,3}\s+/, '');
            const sizes: Record<number, string> = { 1: '16px', 2: '14px', 3: '13px' };
            html.push(`<h${level} style="font-size:${sizes[level]};font-weight:800;margin:14px 0 6px;color:#1a1a1a;">${inlineFormat(headerText)}</h${level}>`);
            i++; continue;
        }

        // Blockquote: > text (collect consecutive lines)
        if (trimmed.startsWith('> ')) {
            const quoteLines: string[] = [];
            while (i < lines.length && lines[i]!.trim().startsWith('> ')) {
                quoteLines.push(inlineFormat(lines[i]!.trim().substring(2)));
                i++;
            }
            html.push(`<blockquote style="border-left:3px solid #d1d5db;padding:6px 12px;margin:8px 0;color:#6b7280;font-style:italic;">${quoteLines.join('<br/>')}</blockquote>`);
            continue;
        }

        // Unordered list: - item or * item (collect consecutive)
        if (/^[-*]\s/.test(trimmed)) {
            const items: string[] = [];
            while (i < lines.length && /^[-*]\s/.test(lines[i]!.trim())) {
                items.push(`<li>${inlineFormat(lines[i]!.trim().replace(/^[-*]\s+/, ''))}</li>`);
                i++;
            }
            html.push(`<ul style="margin:8px 0;padding-left:24px;">${items.join('')}</ul>`);
            continue;
        }

        // Ordered list: 1. item (collect consecutive)
        if (/^\d+\.\s/.test(trimmed)) {
            const items: string[] = [];
            while (i < lines.length && /^\d+\.\s/.test(lines[i]!.trim())) {
                items.push(`<li>${inlineFormat(lines[i]!.trim().replace(/^\d+\.\s+/, ''))}</li>`);
                i++;
            }
            html.push(`<ol style="margin:8px 0;padding-left:24px;">${items.join('')}</ol>`);
            continue;
        }

        // Regular paragraph — collect consecutive non-empty, non-special lines.
        // Guard 1 — ALWAYS consume the current line first so `i` advances even
        // for a line that leads with a block-marker char but matched no block
        // branch above (e.g. "--- Site Report.docx ---", "*** note ***").
        const paraLines: string[] = [inlineFormat(trimmed)];
        i++;
        while (i < lines.length && lines[i]!.trim() && !/^(#{1,3}\s|[-*]\s|>\s|\d+\.\s|-{3,}|_{3,}|\*{3,})/.test(lines[i]!.trim())) {
            paraLines.push(inlineFormat(lines[i]!.trim()));
            i++;
        }
        html.push(`<p style="margin:6px 0;">${paraLines.join('<br/>')}</p>`);
    }

    return html.join('');
}

export function formatTranscriptBlocks(text: string | null | undefined): string {
    if (!text || typeof text !== 'string') return text || '';
    const markerRe = /-{3,}\s*(VIDEO TRANSCRIPT|AUDIO TRANSCRIPT|Audio|VOICE NOTE):\s*(.+?)\s*-{3,}/gi;
    const marks: Array<{ start: number; end: number; kind: string; name: string }> = [];
    let m: RegExpExecArray | null;
    while ((m = markerRe.exec(text)) !== null) {
        marks.push({ start: m.index, end: m.index + m[0].length, kind: m[1] || '', name: (m[2] || '').trim() });
    }
    if (marks.length === 0) return text;
    const toBullets = (body: string): string => {
        const cleaned = String(body || '').trim().replace(/\s+/g, ' ');
        if (!cleaned) return '_(empty transcript)_';
        let sentences = cleaned.split(/(?<=[.!?])\s+(?=[A-Z0-9"\u201C])/);
        sentences = sentences.flatMap(s => (s.length > 220 ? s.split(/,\s+(?=[A-Z])/) : [s]));
        const validSentences = sentences.map(s => s.trim()).filter(Boolean).slice(0, 80);
        if (validSentences.length === 0) return cleaned;
        return validSentences.map(s => `- ${s}`).join('\n');
    };
    const prettyKind = (k: string): string => {
        const up = (k || '').toUpperCase();
        if (up.includes('VIDEO')) return 'Video';
        if (up.includes('VOICE')) return 'Voice Note';
        if (up.includes('AUDIO')) return 'Audio';
        return k;
    };
    const out: string[] = [];
    let cursor = 0;
    for (let i = 0; i < marks.length; i++) {
        const mk = marks[i];
        if (!mk) continue;
        if (i === 0 && mk.start > cursor) {
            const pre = text.slice(cursor, mk.start).trim();
            if (pre) out.push(pre);
        }
        const nextMark = marks[i + 1];
        const bodyEnd = nextMark ? nextMark.start : text.length;
        const body = text.slice(mk.end, bodyEnd);
        out.push('');
        out.push(`### ${prettyKind(mk.kind)}: ${mk.name}`);
        out.push(toBullets(body));
        cursor = bodyEnd;
    }
    return out.join('\n\n').trim();
}

export function plainTextToNotesHtml(text: string | null | undefined): string {
    if (!text || typeof text !== 'string') return '';
    const prepared = formatTranscriptBlocks(text);
    const hasMarkdown = /(\*\*[^*]+\*\*|^#{1,3}\s|^[-*]\s|^>\s|^\d+\.\s)/m.test(prepared);
    if (hasMarkdown) return markdownToHtml(prepared);
    const escape = (s: string): string => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const paras = prepared.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
    if (paras.length === 0) return '';
    return paras.map(p => '<p>' + escape(p).split('\n').join('<br/>') + '</p>').join('');
}

